import {Integer, Show, VStack} from 'platform/foundation';
import {cva} from 'platform/shared';

import {
  ChangeEvent,
  ForwardedRef,
  forwardRef,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {isNil, isNotNil} from 'ramda';

import {
  Nullish,
  suffixTestId,
  TestIdProps,
  useComposedRef,
  useLatest,
  useOnMount,
} from 'shared/web';

import {FormControlProps} from '../../types/FormControlProps';
import {TextEditorReadyHandler, TextEditorSelection} from '../../types/TextEditorApi';
import {TextEditorContentProps} from '../../types/TextEditorContentProps';
import {normalizeTextEditorSelection} from '../../utils/normalizeTextEditorSelection';
import {HelperText} from '../HelperText/HelperText';
import {Label} from '../Label/Label';
import {LabelActions} from '../Label/LabelActions';
import {FooterBar, FooterContent} from '../RichText/components/FooterBar';
import {TextEditorFrame} from '../TextEditorFrame/TextEditorFrame';
import {TextEditorOverlays} from '../TextEditorFrame/TextEditorOverlays';
import {useFontsLoadedListener} from './hooks/useFontsLoadedListener';
import {useWindowResizeListener} from './hooks/useWindowResizeListener';
import {calculateNodeHeight} from './utils/calculateNodeHeight';
import {getSizingData, SizingData} from './utils/getSizingData';

export interface TextareaProps
  extends
    FormControlProps<string | null, HTMLTextAreaElement>,
    TestIdProps,
    TextEditorContentProps {
  isResizable?: boolean;
  isScrollable?: boolean;
  placeholder?: string;
  maxLength?: number;
  maxRows?: Integer | Nullish;
  minRows?: Integer | Nullish;
  rows?: Integer;
  isRecommended?: boolean;
  labelActions?: LabelActions;
  isCounterVisible?: boolean;
  isBorderless?: boolean;
  'aria-label'?: string;
  /**
   * Receives this field as a plain-text editor, so a caller can rewrite one selected part without
   * reaching into the element. Offsets are plain-text character offsets.
   */
  onEditorReady?: TextEditorReadyHandler;
}

export const Textarea = forwardRef(
  (props: TextareaProps, userRef: ForwardedRef<HTMLTextAreaElement>) => {
    const id = useId();
    const libRef = useRef<HTMLTextAreaElement>(null);
    const ref = useComposedRef(libRef, userRef);
    const heightRef = useRef(0);
    const measurementsCacheRef = useRef<SizingData>(null);

    const valueRef = useLatest(props.value ?? '');
    const onChangeRef = useLatest(props.onChange);
    const onEditorReadyRef = useLatest(props.onEditorReady);

    const currentValue = props.value ?? '';
    const [lastSelection, setLastSelection] = useState<TextEditorSelection | null>(null);
    const [lastKnownValue, setLastKnownValue] = useState(currentValue);
    const lastSelectionRef = useLatest(lastSelection);

    if (lastKnownValue !== currentValue) {
      setLastKnownValue(currentValue);
      setLastSelection(null);
    }

    // A range is remembered at all because a control outside the field takes focus before it can
    // be clicked. An empty range means the caret only moved, so it drops the range too.
    const handleSelect = () => {
      const node = libRef.current;

      if (isNil(node)) {
        return;
      }

      const selection = {
        index: node.selectionStart,
        length: node.selectionEnd - node.selectionStart,
      };
      setLastSelection(
        normalizeTextEditorSelection(selection, (index) => node.value[index] === '\n')
      );
    };

    useOnMount(
      () => {
        onEditorReadyRef.current?.({
          getSelection: () => lastSelectionRef.current,
          getContent: (selection) =>
            isNil(selection)
              ? valueRef.current
              : valueRef.current.slice(selection.index, selection.index + selection.length),
          replaceSelection: (selection, content) => {
            const current = valueRef.current;
            const next = `${current.slice(0, selection.index)}${content}${current.slice(selection.index + selection.length)}`;

            onChangeRef.current?.(next);

            return next;
          },
          focus: () => libRef.current?.focus(),
        });
      },
      () => onEditorReadyRef.current?.(null)
    );

    const resizeTextarea = () => {
      const node = libRef.current;
      if (!node) {
        return;
      }
      const nodeSizingData = getSizingData(node);

      if (!nodeSizingData) {
        return;
      }

      measurementsCacheRef.current = nodeSizingData;

      const [height] = calculateNodeHeight(
        nodeSizingData,
        node.value || node.placeholder,
        props.minRows ?? props.rows,
        props.maxRows ?? props.rows
      );

      if (heightRef.current !== height) {
        heightRef.current = height;
        node.style.setProperty('height', `${height}px`, 'important');
      }
    };

    // eslint-disable-next-line react/exhaustive-deps
    useLayoutEffect(resizeTextarea, [props.value, props.minRows, props.maxRows, props.rows]);
    useWindowResizeListener(resizeTextarea);
    useFontsLoadedListener(resizeTextarea);

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
      props.onChange?.(event.target.value, event);
    };
    return (
      <VStack>
        <Label
          id={id}
          isRequired={props.isRequired}
          tooltip={props.tooltip}
          isRecommended={props.isRecommended}
          data-testid={suffixTestId('label', props)}
          actions={props.labelActions}
          maxLength={props.maxLength}
          isCounterVisible={props.isCounterVisible}
          currentLength={props.value?.length}
        >
          {props.label}
        </Label>
        <TextEditorFrame
          onKeyDownCapture={props.onKeyDownCapture}
          $isBorderless={isNil(props.editorContent)}
          $isDisabled={props.isDisabled}
          $isInvalid={props.isInvalid}
        >
          <textarea
            className={textareaVariants({
              isResizable: props.isResizable,
              isScrollable: props.isScrollable,
              isInvalid: props.isInvalid,
              isRecommended: props.isRecommended,
              isBorderless: !isNil(props.editorContent) || props.isBorderless,
            })}
            id={id}
            aria-label={props['aria-label']}
            placeholder={props.placeholder}
            maxLength={props.maxLength}
            value={props.value ?? ''}
            onChange={handleChange}
            onSelect={handleSelect}
            onBlur={props.onBlur}
            onFocus={props.onFocus}
            onKeyDown={props.onKeyDown}
            name={props.name}
            disabled={props.isDisabled}
            ref={ref}
            data-testid={suffixTestId('textareaInput', props)}
          />
          <Show
            when={
              !props.isDisabled &&
              (!isNil(props.selectionContent) || !isNil(props.editorHighlights))
            }
          >
            <TextEditorOverlays
              getEditor={() => libRef.current}
              label={props.label}
              selectionContent={props.selectionContent}
              editorHighlights={props.editorHighlights}
            />
          </Show>
          <Show when={isNotNil(props.footerContent)}>
            <FooterBar>
              <FooterContent>{props.footerContent}</FooterContent>
            </FooterBar>
          </Show>
          {props.editorContent}
        </TextEditorFrame>
        <Show when={props.errorMessage ?? props.helperText}>
          <HelperText
            errorMessage={props.errorMessage}
            helperText={props.helperText}
            data-testid={suffixTestId('helper', props)}
          />
        </Show>
      </VStack>
    );
  }
);

// tw-migration: kept for API parity with the old barrel; the styles live in textareaVariants below
export type StyledTextareaProps = {
  $isResizable?: TextareaProps['isResizable'];
  $isScrollable?: TextareaProps['isScrollable'];
  $isInvalid?: TextareaProps['isInvalid'];
  $isRecommended?: TextareaProps['isRecommended'];
  $isBorderless?: boolean;
};

const textareaVariants = cva(
  'block w-full rounded-[var(--radius-small)] border border-solid px-2 py-[5px] ![font-size:var(--text-size-small)] leading-[var(--text-leading-small)] font-[var(--text-weight-default)] tracking-[0] text-[var(--color-text-primary)] outline-none placeholder:text-[var(--color-text-tertiary)] hover:border hover:border-solid hover:border-[var(--color-severity-informational)] hover:bg-[var(--color-palettes-white-10-100)] active:border-[var(--color-severity-informational)] active:[box-shadow:var(--shadow-active)] focus:border-[var(--color-severity-informational)] focus:bg-[var(--color-palettes-white-10-100)] focus:[box-shadow:var(--shadow-active)] disabled:cursor-not-allowed disabled:border-[var(--color-palettes-neutral-70-40)] disabled:bg-[var(--color-palettes-white-10-40)] disabled:[-webkit-text-fill-color:var(--color-text-primary)] disabled:[box-shadow:none] disabled:hover:border-[var(--color-palettes-neutral-70-40)] disabled:focus:border-[var(--color-palettes-neutral-70-40)] disabled:focus:[box-shadow:none] [&:-webkit-autofill]:bg-[var(--color-palettes-yellow-20-100)]',
  {
    variants: {
      isResizable: {
        true: 'resize-y',
        false: 'resize-none',
      },
      isScrollable: {
        true: 'overflow-y-auto',
        false: 'overflow-y-hidden',
      },
      isInvalid: {
        true: 'border-[var(--color-severity-danger)]',
        false: 'border-[var(--color-palettes-neutral-70-100)]',
      },
      isRecommended: {
        true: 'bg-[var(--color-palettes-orange-10-100)]',
        false: 'bg-[var(--color-palettes-white-10-100)]',
      },
      isBorderless: {
        true: '!border-0 !bg-transparent ![box-shadow:none] hover:!border-0 hover:!bg-transparent hover:![box-shadow:none] focus:!border-0 focus:!bg-transparent focus:![box-shadow:none] active:!border-0 active:!bg-transparent active:![box-shadow:none] disabled:!border-0 disabled:!bg-transparent disabled:![box-shadow:none]',
        false: '',
      },
    },
    defaultVariants: {
      isResizable: false,
      isScrollable: false,
      isInvalid: false,
      isRecommended: false,
      isBorderless: false,
    },
  }
);
